import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTeacherDashboard } from '../../services/analytics';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, GraduationCap, FileText, BarChart3, Star, ArrowRight } from 'lucide-react';

function StatCard({ icon, label, value, color }) {
  const bg = { blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300', green: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300', purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300', orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className={`w-10 h-10 rounded-xl ${bg[color]} flex items-center justify-center text-lg mb-3`}>{icon}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTeacherDashboard().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
        <h1 className="text-2xl font-bold mb-2">O'qituvchi paneli</h1>
        <p className="text-indigo-100">Xush kelibsiz, {user?.name}!</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<BookOpen className="w-5 h-5" />} label="Jami kurslar" value={data?.total_courses || 0} color="blue" />
        <StatCard icon={<GraduationCap className="w-5 h-5" />} label="Jami talabalar" value={data?.total_students || 0} color="green" />
        <StatCard icon={<FileText className="w-5 h-5" />} label="Jami yozilishlar" value={data?.total_enrollments || 0} color="purple" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="O'rtacha tugatish" value={(data?.avg_completion_rate || 0) + '%'} color="orange" />
      </div>

      {/* Course stats */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Kurslarim</h2>
          <Link to="/teacher/courses" className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:text-blue-700">Barchasi →</Link>
        </div>
        {data?.course_stats?.length > 0 ? (
          <div className="space-y-3">
            {data.course_stats.map(c => (
              <div key={c.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{c.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{c.enrollments_count} talaba</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-yellow-600 flex items-center gap-1"><Star className="w-4 h-4" /> {c.rating || '—'}</span>
                  <Link to={`/teacher/courses/${c.id}`} className="text-blue-600 text-sm font-medium">Tahrirlash →</Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400 mb-4">Hali kurs yaratmagansiz</p>
            <Link to="/teacher/courses" className="inline-flex px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">Yangi kurs yaratish</Link>
          </div>
        )}
      </div>

      {/* Recent enrollments */}
      {data?.recent_enrollments?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">So'nggi yozilishlar</h2>
          <div className="space-y-3">
            {data.recent_enrollments.slice(0, 5).map(e => (
              <div key={e.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 flex items-center justify-center text-sm font-bold">
                  {e.user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{e.user?.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{e.course?.title}</p>
                </div>
                <span className="text-xs text-gray-400">{new Date(e.created_at).toLocaleDateString('uz')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}